import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";
import { SEO_BASE_URL, internalLinkTargets } from "../../data/seoPages";

function prettifyPath(pathname) {
  return pathname
    .replace(/\//g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");
}

function SeoPlaceholderPage({ path }) {
  const title = `Bellhopt ${prettifyPath(path)} | Placeholder`;
  const description = `Placeholder route for ${path}. This page is intentionally noindex until production copy is finalized.`;
  const canonical = `${SEO_BASE_URL}${path}/`;

  const breadcrumbs = path
    .split("/")
    .filter(Boolean)
    .map((name, index, arr) => {
      const itemPath = `/${arr.slice(0, index + 1).join("/")}/`;
      return {
        "@type": "ListItem",
        position: index + 1,
        name: name.replace(/-/g, " "),
        item: `${SEO_BASE_URL}${itemPath}`,
      };
    });

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs,
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical={`${path}/`}
        robots="noindex,follow"
        ogTitle={title}
        ogDescription={description}
        structuredData={structuredData}
      />

      <div className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <div className="rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl p-8 mobile:p-5 shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
          <p className="inline-block rounded-full bg-[#F9FAFB] border border-[#E5E7EB] px-4 py-2 text-[12px] font-semibold tracking-[0.1em] uppercase text-[#111827]">
            Noindex Placeholder
          </p>
          <h1 className="font-segoe font-semibold text-[36px] mobile:text-[26px] text-[#111827] mt-4">
            {prettifyPath(path)}
          </h1>
          <p className="text-[#6B7280] text-[18px] mobile:text-[16px] mt-3 leading-7">
            This route is live for architecture and internal linking, but intentionally set to
            noindex until final content is complete.
          </p>

          <div className="mt-7 border border-[#E5E7EB] rounded-2xl p-5 bg-white/85">
            <h2 className="font-segoe font-semibold text-[22px]">TODO Content Brief</h2>
            <ul className="mt-3 text-[16px] text-[#6B7280] leading-8 list-disc pl-6">
              <li>TODO: Add unique long-form copy for this route.</li>
              <li>TODO: Add route-specific proof points and conversion CTAs.</li>
              <li>TODO: Add FAQs and switch robots from noindex to index.</li>
              <li>Constraint: Always use Bellhopt Concierge wording.</li>
              <li>Constraint: Include 24-hour lead time required where relevant.</li>
            </ul>
          </div>

          <div className="mt-7">
            <h3 className="font-segoe font-semibold text-[20px] mb-3">Internal Links</h3>
            <div className="flex flex-wrap gap-3">
              {internalLinkTargets.map((linkItem) => (
                <Link
                  key={linkItem.to}
                  to={linkItem.to}
                  className="px-4 py-2 rounded-full border border-[#E5E7EB] bg-white text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase hover:bg-[#F9FAFB] transition-all duration-200"
                >
                  {linkItem.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SeoMarketingShell>
  );
}

export default SeoPlaceholderPage;
